import { useState, useEffect, useRef } from "react";
import { homeFaqItems } from "../../../mocks/homeFaq";

export default function HomeFaqSection() {
  const [openId, setOpenId] = useState<number | null>(1);
  const [copied, setCopied] = useState(false);
  const faqRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  const scrollToFaq = (id: number) => {
    setOpenId(id);
    setTimeout(() => {
      const el = faqRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const handleCopyLine = async () => {
    try {
      await navigator.clipboard.writeText("https://lin.ee/OpssTIM");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="py-14 md:py-20 bg-cream">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="tag-label mb-3 inline-block">FAQ</span>
          <h2 className="mb-3">自地自建常見問題</h2>
          <p className="text-medium-gray max-w-2xl mx-auto">
            我們整理了業主最關心的 6 大問題，幫助你在開始前先建立正確概念。歡迎加入 LINE 諮詢更多細節。
          </p>
        </div>

        {/* Quick Question Cards */}
        <div className="mb-8 md:mb-10">
          <p className="text-sm text-medium-gray mb-4 text-center">你可能會想問...</p>
          <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hide">
            {homeFaqItems.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToFaq(item.id)}
                className={`group shrink-0 snap-start w-[200px] md:w-[220px] text-left bg-white rounded-xl p-4 border transition-all duration-300 cursor-pointer ${
                  openId === item.id
                    ? "border-champagne/60 bg-champagne/5"
                    : "border-warm-gray/30 hover:border-champagne/40 hover:-translate-y-0.5"
                }`}
              >
                <span
                  className={`w-9 h-9 flex items-center justify-center rounded-full mb-3 transition-all duration-300 ${
                    openId === item.id
                      ? "bg-champagne text-graphite"
                      : "bg-champagne/10 text-champagne group-hover:bg-champagne/20"
                  }`}
                >
                  <i className={`${item.icon} text-base`} />
                </span>
                <p className="text-graphite text-sm font-semibold leading-snug line-clamp-2 group-hover:text-champagne transition-colors">
                  {item.question}
                </p>
                <span className="text-xs text-medium-gray mt-2 inline-flex items-center gap-1">
                  <i className="ri-arrow-down-line" />
                  查看回答
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {homeFaqItems.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                ref={(el) => {
                  faqRefs.current[item.id] = el;
                }}
                className={`bg-white rounded-xl border transition-all duration-300 ${
                  isOpen ? "border-champagne/40 shadow-sm" : "border-warm-gray/20 hover:border-warm-gray/40"
                }`}
              >
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-start gap-3 px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer group"
                >
                  <span
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                      isOpen
                        ? "bg-champagne text-graphite"
                        : "bg-champagne/10 text-champagne group-hover:bg-champagne/20"
                    }`}
                  >
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <span
                    className={`font-semibold text-base md:text-base leading-snug pt-1.5 transition-colors duration-200 ${
                      isOpen ? "text-graphite" : "text-graphite group-hover:text-champagne"
                    }`}
                  >
                    {item.question}
                  </span>
                  <span className="ml-auto shrink-0 w-7 h-7 rounded-full bg-champagne/10 flex items-center justify-center mt-1 transition-all duration-300">
                    <i
                      className={`ri-${isOpen ? "subtract" : "add"}-line text-champagne text-sm transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    isOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-5 pb-5 md:px-6 md:pb-6">
                    <div className="flex gap-4">
                      <div className="w-10 shrink-0" />
                      <div>
                        <p className="text-medium-gray text-base leading-relaxed mb-0">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Area */}
        <div className="text-center mt-10 md:mt-12">
          <p className="text-medium-gray text-base mb-4">
            還有其他問題？加入 LINE 與專人直接對話
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://lin.ee/OpssTIM"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary gap-2 inline-flex"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill" />
              </span>
              加入 LINE@ 諮詢
            </a>
            <a
              href="/seminars"
              className="btn-secondary gap-2 inline-flex"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-event-line" />
              </span>
              預約免費說明會
            </a>
          </div>
          <button
            onClick={handleCopyLine}
            className="mt-4 text-sm text-medium-gray hover:text-champagne transition-colors cursor-pointer inline-flex items-center gap-1.5"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? "ri-check-line text-emerald-600" : "ri-clipboard-line"} />
            </span>
            {copied ? "已複製 LINE 連結" : "複製 LINE 連結分享給家人"}
          </button>
        </div>
      </div>
    </section>
  );
}